import { ArrowLeft, ExternalLink, Heart, Calendar, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/ProductPage.css';

const Saivor = () => {
  return (
    <div className="product-page saivor-page">
      {/* Header */}
      <header className="product-header" data-testid="product-header">
        <nav className="product-nav" data-testid="product-nav">
          <Button 
            onClick={() => window.location.href = '/'} 
            variant="ghost" 
            className="back-btn"
            data-testid="back-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className="product-hero saivor-hero" data-testid="hero-section">
        <div className="hero-badge saivor-badge" data-testid="hero-badge">LIVE</div>
        <h1 className="product-hero-title" data-testid="hero-title">Saivor</h1>
        <p className="product-hero-tagline" data-testid="hero-tagline">
          Eat without the stress
        </p>
        <p className="hero-description" data-testid="hero-description">
          Saivor shows you what to eat based on your health, goals, and what you've already eaten. 
          So you can stop guessing and enjoy food again.
        </p>
      </section>

      {/* How It Works */}
      <section className="product-features" data-testid="features-section">
        <div className="container">
          <h2 className="features-title" data-testid="features-title">How It Works</h2>
          <div className="features-grid">
            <div className="feature-item" data-testid="feature-log-food">
              <div className="feature-number saivor-number">1</div>
              <h3>Log Food Easily</h3>
              <p>Say it out loud, search for it, or snap a photo. Calories and nutrition tracked for you.</p>
            </div>

            <div className="feature-item" data-testid="feature-know-needs">
              <div className="feature-number saivor-number">2</div>
              <h3>Know What Your Body Needs</h3>
              <p>We show what you're missing (like fiber, energy, or protein) and suggest meals that match your health needs.</p>
            </div>

            <div className="feature-item" data-testid="feature-shopping-list">
              <div className="feature-number saivor-number">3</div>
              <h3>Get a Smart Shopping List</h3>
              <p>The app builds your list from your updated daily needs. No more buying food you won't use or can't eat.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section" data-testid="benefits-section">
        <div className="container">
          <h2 className="benefits-title" data-testid="benefits-title">Perfect If You...</h2>
          <div className="benefits-grid">
            <div className="benefit-item" data-testid="benefit-stress">
              <Heart className="benefit-icon saivor-icon" size={40} />
              <p>Feel stressed trying to eat 'right'</p>
            </div>
            <div className="benefit-item" data-testid="benefit-intolerances">
              <Heart className="benefit-icon saivor-icon" size={40} />
              <p>Have food intolerances or digestive issues</p>
            </div>
            <div className="benefit-item" data-testid="benefit-conditions">
              <Heart className="benefit-icon saivor-icon" size={40} />
              <p>Live with conditions like reflux, IBS, or diabetes</p>
            </div>
            <div className="benefit-item" data-testid="benefit-health">
              <Heart className="benefit-icon saivor-icon" size={40} />
              <p>Need to eat for your health, but want it to feel normal</p>
            </div>
            <div className="benefit-item" data-testid="benefit-variety">
              <Calendar className="benefit-icon saivor-icon" size={40} />
              <p>Are tired of repeating the same meals over and over</p>
            </div>
            <div className="benefit-item" data-testid="benefit-shopping">
              <ShoppingCart className="benefit-icon saivor-icon" size={40} />
              <p>Want to simplify grocery shopping and meal planning</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="story-section" data-testid="story-section">
        <div className="container">
          <h2 className="story-title" data-testid="story-title">Why I Built This</h2>
          <div className="story-content">
            <p className="story-quote" data-testid="story-quote">
              "I cried on holiday because keeping up with what I needed to eat was just too much."
            </p>
            <p className="story-text" data-testid="story-text">
              Not just because of my health problems, but because every meal felt like a battle. 
              I was trying to get the nutrition I needed, but kept gaining weight.
            </p>
            <p className="story-text" data-testid="story-text-2">
              We tracked meals on a whiteboard and followed meal plans. It worked, but only if we ate the same things every week. 
              That made food feel like a chore.
            </p>
            <p className="story-text" data-testid="story-text-3">
              I built this to take the pressure off. So I wasn't anxious about what to eat, all through the day. 
              It shows what your body still needs, suggests what to eat, and builds a shopping list that changes with you. 
              Eating feels good again.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section saivor-cta-section" data-testid="cta-section">
        <h2 className="cta-title" data-testid="cta-title">Make Eating Enjoyable Again</h2>
        <p className="cta-description" data-testid="cta-description">
          Start your free 14-day trial. No credit card needed. Just see if it works for you.
        </p>
        <div className="cta-buttons" data-testid="cta-buttons">
          <a 
            href="https://saivor.co/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-button saivor-cta-btn"
            data-testid="cta-visit-website"
          >
            <ExternalLink size={20} /> Start Free Trial
          </a>
        </div>
        <p className="cta-pricing" data-testid="cta-pricing">
          £10/month after trial · Cancel anytime
        </p>
      </section>
    </div>
  );
};

export default Saivor;